import React, { useState, useEffect, useRef, useMemo } from 'react';

const LOG_TELEMETRY_DEFAULTS = {
  connected: false,
  armed: false,
  mode: 'N/A',
  lat: 0,
  lon: 0,
  alt: 0,
  alt_msl: 0,
  relative_alt: 0,
  alt_raw: 0,
  relative_alt_raw: 0,
  groundspeed: 0,
  airspeed: 0,
  heading: 0,
  battery_voltage: 0,
  battery_remaining: 0,
  battery_current: 0,
  gps_fix: 0,
  satellites: 0,
  roll: 0,
  pitch: 0,
  yaw: 0,
  throttle: 0,
  climbRate: 0,
  wpDist: 0,
};

const LOG_KEY_ORDER = Object.keys(LOG_TELEMETRY_DEFAULTS);

function TelemetryLogs({ telemetry, droneId }) {
  const [logsPerDrone, setLogsPerDrone] = useState({});
  const [autoScroll, setAutoScroll] = useState(true);
  const logsEndRef = useRef(null);
  const logsContainerRef = useRef(null);
  const prevTelemetryRef = useRef(null);
  const lastLogTimeRef = useRef(0);

  const currentLogs = useMemo(() => droneId ? (logsPerDrone[droneId] || []) : [], [droneId, logsPerDrone]);

  useEffect(() => {
    // Scroll only this log's own container — never scrollIntoView, which scrolls
    // ancestors and would drag an off-screen drawer back into view.
    if (autoScroll && logsContainerRef.current) {
      const c = logsContainerRef.current;
      c.scrollTop = c.scrollHeight;
    }
  }, [currentLogs, autoScroll]);

  useEffect(() => {
    if (!telemetry || !droneId) return;
    // Skip if same reference (deduped by useTelemetry)
    if (prevTelemetryRef.current === telemetry) return;
    prevTelemetryRef.current = telemetry;

    // Throttle: max 1 log entry per second
    const now = Date.now();
    if (now - lastLogTimeRef.current < 1000) return;
    lastLogTimeRef.current = now;

    const timestamp = new Date().toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });

    const logEntry = {
      timestamp,
      data: { ...LOG_TELEMETRY_DEFAULTS, ...telemetry }
    };

    setLogsPerDrone(prev => {
      const existing = prev[droneId] || [];
      // Cap at 200 entries to prevent memory issues
      const trimmed = existing.length >= 200 ? existing.slice(-199) : existing;
      return {
        ...prev,
        [droneId]: [...trimmed, logEntry]
      };
    });
  }, [telemetry, droneId]);

  const handleScroll = () => {
    if (logsContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      setAutoScroll(isAtBottom);
    }
  };

  const clearLogs = () => {
    if (!droneId) return;
    setLogsPerDrone(prev => ({
      ...prev,
      [droneId]: []
    }));
  };

  const formatValue = (key, value) => {
    if (value === undefined || value === null) return 'N/A';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') {
      if (key.includes('lat') || key.includes('lon')) return value.toFixed(6);
      if (key.includes('alt') || key.includes('speed') || key === 'climbRate') return value.toFixed(2);
      if (key.includes('roll') || key.includes('pitch') || key.includes('yaw')) return value.toFixed(3);
      if (key.includes('battery_voltage')) return value.toFixed(2);
      if (key.includes('battery_current')) return value.toFixed(2);
      if (key.includes('heading')) return value.toFixed(1);
      if (key.includes('throttle_percent')) return `${value.toFixed(1)}%`;
      if (key === 'throttle') return `${value}%`;
      if (key.includes('battery_remaining')) return `${value}%`;
    }
    return String(value);
  };

  const getValueColor = (key, value) => {
    if (key === 'connected') return value ? 'var(--success)' : 'var(--danger)';
    if (key === 'armed') return value ? 'var(--warning)' : 'var(--success)';
    if (key === 'gps_fix') return value >= 3 ? 'var(--success)' : 'var(--danger)';
    if (key === 'battery_remaining') {
      if (value > 50) return 'var(--success)';
      if (value > 20) return 'var(--warning)';
      return 'var(--danger)';
    }
    if (key === 'throttle_percent') {
      if (value > 75) return 'var(--warning)';
      if (value > 50) return 'var(--accentSecondary)';
      if (value > 25) return 'var(--success)';
      return 'var(--textSecondary)';
    }
    return 'var(--textPrimary)';
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'var(--bgPrimary)',
      overflow: 'hidden',
      margin: 0,
      padding: 0
    }}>
      {/* Compact Header */}
      <div style={{
        padding: '8px 16px',
        background: 'var(--bgSecondary)',
        borderBottom: '1px solid var(--borderSecondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px 12px',
        flexShrink: 0,
        margin: 0
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px 12px'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--accent)',
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.02em',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            Live Telemetry Log
          </div>
          {droneId && (
            <span style={{
              padding: '2px 8px',
              background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
              color: 'var(--accent)',
              fontSize: '11px',
              fontWeight: 600,
              fontFamily: 'var(--font-label)',
              borderRadius: '6px',
              border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)'
            }}>
              {droneId.length > 20 ? droneId.substring(0, 18) + '...' : droneId}
            </span>
          )}
          <span style={{
            color: 'var(--textMuted)',
            fontSize: '11px',
            background: 'color-mix(in srgb, var(--textPrimary) 4%, transparent)',
            padding: '2px 7px',
            borderRadius: '10px',
            border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)'
          }}>
            {currentLogs.length} entries
          </span>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--textSecondary)',
            fontSize: '11.5px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              style={{ accentColor: 'var(--accent)' }}
            />
            Auto-scroll
          </label>
          <button
            onClick={clearLogs}
            disabled={!droneId || currentLogs.length === 0}
            style={{
              width: 'auto',
              margin: 0,
              padding: '4px 10px',
              background: currentLogs.length === 0 ? 'color-mix(in srgb, var(--textPrimary) 3%, transparent)' : 'color-mix(in srgb, var(--danger) 12%, transparent)',
              color: currentLogs.length === 0 ? 'var(--textMuted)' : '#f87171',
              border: `1px solid ${currentLogs.length === 0 ? 'color-mix(in srgb, var(--textPrimary) 8%, transparent)' : 'color-mix(in srgb, var(--danger) 30%, transparent)'}`,
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: 600,
              cursor: currentLogs.length === 0 ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.15s ease',
            }}
            title="Clear live telemetry buffer"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            Clear
          </button>
        </div>
      </div>

      {/* Logs Content */}
      <div
        ref={logsContainerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '4px 8px 0 8px',
          fontFamily: 'monospace',
          fontSize: '11px',
          minHeight: 0,
          margin: 0
        }}
      >
        {currentLogs.length === 0 ? (
          <div style={{
            color: 'var(--textMuted)',
            textAlign: 'center',
            padding: '40px',
            fontSize: '12px'
          }}>
            {droneId ? 'Waiting for telemetry data...' : 'Select a drone to view telemetry logs'}
          </div>
        ) : (
          currentLogs.map((log, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                gap: '8px',
                padding: '4px 8px',
                borderBottom: '1px solid var(--bgSecondary)',
                background: index % 2 === 0 ? 'transparent' : 'var(--bgTertiary)'
              }}
            >
              <span style={{ color: 'var(--textMuted)', flexShrink: 0 }}>
                [{log.timestamp}]
              </span>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {[...LOG_KEY_ORDER, ...Object.keys(log.data).filter((k) => !LOG_KEY_ORDER.includes(k))].map((key) => {
                  const value = log.data[key];
                  return (
                  <span key={key}>
                    <span style={{ color: 'var(--textSecondary)' }}>{key}:</span>
                    <span style={{ color: getValueColor(key, value), marginLeft: '4px' }}>
                      {formatValue(key, value)}
                    </span>
                  </span>
                  );
                })}
              </div>
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
}

export default TelemetryLogs;
